'use client';

import type { RefObject } from 'react';
import { useEffect, useState } from 'react';

interface IntersectionObserverResult {
  isIntersecting: boolean;
  entry: IntersectionObserverEntry | null;
}

interface UseIntersectionObserverOptions extends IntersectionObserverInit {
  /** Keep `isIntersecting` true after the first visible observation. */
  once?: boolean;
  /** Delay observation until the caller has rendered the ref target. */
  enabled?: boolean;
}

export function useIntersectionObserver(
  ref: RefObject<Element | null>,
  options?: UseIntersectionObserverOptions,
): IntersectionObserverResult {
  const [isIntersecting, setIsIntersecting] = useState<boolean>(false);
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);

  // Decompose options into primitive deps so that the typical call site
  // `useIntersectionObserver(ref, { rootMargin: '200px' })` — which passes a
  // fresh object literal every render — doesn't thrash the observer. Without
  // this, the effect ran on every parent render, disconnecting and rebuilding
  // the IntersectionObserver; at 20+ cards per markets grid that was hundreds
  // of observer allocations per session.
  const root = options?.root ?? null;
  const rootMargin = options?.rootMargin;
  const once = options?.once ?? false;
  const enabled = options?.enabled ?? true;
  // Thresholds can be a number or a number[]; stringify so array identity
  // doesn't re-trigger the effect when the shape is unchanged.
  const thresholdKey =
    options?.threshold === undefined ? undefined : JSON.stringify(options.threshold);

  useEffect(() => {
    if (!enabled) return;
    const element = ref.current;
    if (element === null) return;

    const observerInit: IntersectionObserverInit = { root };
    if (rootMargin !== undefined) observerInit.rootMargin = rootMargin;
    if (thresholdKey !== undefined) {
      observerInit.threshold = JSON.parse(thresholdKey) as number | number[];
    }

    const observer = new IntersectionObserver((entries: IntersectionObserverEntry[]): void => {
      const first = entries[0];
      if (first !== undefined) {
        if (first.isIntersecting || !once) setIsIntersecting(first.isIntersecting);
        setEntry(first);
        if (first.isIntersecting && once) observer.disconnect();
      }
    }, observerInit);

    observer.observe(element);

    return (): void => {
      observer.disconnect();
    };
  }, [ref, root, rootMargin, thresholdKey, once, enabled]);

  return { isIntersecting, entry };
}
